<template>
    <div class="relative">
        <div
            class="rounded-lg bg-white p-4 shadow-lg dark:bg-slate-800"
            :class="loading || processing ? 'pointer-events-none opacity-75' : ''"
        >
            <div class="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-3 dark:border-slate-700">
                <div>
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-slate-100">
                        Checklist de Verificação
                    </h3>
                    <p class="text-sm text-gray-500 dark:text-slate-400">
                        Análise automatizada de {{ totalCount }} pontos de controle
                    </p>
                </div>
                <div class="flex items-center gap-3">
                    <div class="flex items-center gap-2">
                        <div class="relative h-10 w-10">
                            <svg class="h-10 w-10 -rotate-90 transform">
                                <circle
                                    cx="20"
                                    cy="20"
                                    r="18"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    fill="none"
                                    class="text-gray-200 dark:text-slate-700"
                                />
                                <circle
                                    cx="20"
                                    cy="20"
                                    r="18"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    fill="none"
                                    :stroke-dasharray="circumference"
                                    :stroke-dashoffset="progressOffset"
                                    class="text-indigo-600 transition-all duration-300 dark:text-indigo-400"
                                />
                            </svg>
                            <span class="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700 dark:text-slate-300">
                                {{ checklistData.final_score || 0 }}
                            </span>
                        </div>
                        <div class="flex">
                            <div>
                                <p class="text-xs font-medium text-gray-500 dark:text-slate-400">Score</p>
                                <p class="text-sm font-semibold" :class="scoreColorClass">
                                    {{ scoreLabel }}
                                </p>
                            </div>
                            <div v-if="checklistData.total_orders" class="ml-3 border-l border-gray-200 pl-3 dark:border-slate-600">
                                <p class="text-xs font-medium text-gray-500 dark:text-slate-400">Pedidos</p>
                                <p class="text-sm font-semibold text-gray-700 dark:text-slate-300">
                                    {{ checklistData.total_orders }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-col items-end gap-1">
                        <div class="rounded-full px-3 py-1 text-xs font-semibold" :class="statusBadgeClass">
                            {{ passedCount }}/{{ totalCount }}
                        </div>
                        <div class="flex items-center gap-2 text-xs">
                            <span v-if="warningCount > 0" class="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                                <ExclamationTriangleIcon class="h-3 w-3" />
                                {{ warningCount }} alerta{{ warningCount > 1 ? 's' : '' }}
                            </span>
                            <span v-if="failedCount > 0" class="flex items-center gap-1 text-red-600 dark:text-red-400">
                                <XCircleIcon class="h-3 w-3" />
                                {{ failedCount }} falha{{ failedCount > 1 ? 's' : '' }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="loading" class="mt-6 flex justify-center py-8">
                <ticto-loading size="md" />
            </div>

            <div v-else-if="error" class="mt-4 rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                <div class="flex">
                    <ExclamationTriangleIcon class="h-5 w-5 text-red-400" />
                    <div class="ml-3">
                        <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
                            Erro ao carregar checklist
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">{{ error }}</p>
                        <button
                            @click="loadChecklist"
                            class="mt-2 text-sm font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                        >
                            Tentar novamente
                        </button>
                    </div>
                </div>
            </div>

            <div v-else-if="checklistData.checks" class="mt-4">
                <div v-if="checklistData.recommendation" class="mb-4 rounded-lg p-3" :class="recommendationClass">
                    <div class="flex items-start">
                        <InformationCircleIcon class="mt-0.5 h-5 w-5 flex-shrink-0" :class="recommendationIconClass" />
                        <p class="ml-2 text-sm font-medium" :class="recommendationTextClass">
                            {{ checklistData.recommendation }}
                        </p>
                    </div>
                </div>

                <!-- Filter controls -->
                <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div class="flex flex-wrap gap-2">
                        <button
                            @click="filterStatus = 'issues'"
                            class="rounded-full px-3 py-1 text-xs font-medium transition-colors"
                            :class="filterStatus === 'issues'
                                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'"
                        >
                            Apenas Problemas ({{ issuesCount }})
                        </button>
                        <button
                            @click="filterStatus = 'all'"
                            class="rounded-full px-3 py-1 text-xs font-medium transition-colors"
                            :class="filterStatus === 'all'
                                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'"
                        >
                            Todos ({{ totalCount }})
                        </button>
                    </div>

                    <!-- Category badges -->
                    <div v-if="checklistData.categories" class="flex flex-wrap gap-2">
                        <div
                            v-for="(category, key) in checklistData.categories"
                            :key="key"
                            class="flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium"
                            :class="getCategoryBadgeClass(category.checks)"
                        >
                            <component
                                :is="getCategoryIcon(category.icon)"
                                class="h-3 w-3"
                            />
                            <span>{{ category.label }}</span>
                            <span class="ml-1 font-semibold">
                                {{ getCategoryPassedCount(category.checks) }}/{{ category.checks.length }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- All checks list -->
                <div class="space-y-2">
                    <div
                        v-for="check in filteredChecks"
                        :key="check.id"
                        class="flex items-start gap-3 rounded-lg border p-3 transition-colors"
                        :class="getCheckBorderClass(check.status)"
                    >
                        <div class="mt-0.5 flex-shrink-0">
                            <CheckCircleIcon
                                v-if="check.status === 'passed'"
                                class="h-5 w-5 text-green-500"
                            />
                            <XCircleIcon
                                v-else-if="check.status === 'failed'"
                                class="h-5 w-5 text-red-500"
                            />
                            <ExclamationTriangleIcon
                                v-else
                                class="h-5 w-5 text-yellow-500"
                            />
                        </div>
                        <div class="flex flex-1 flex-col">
                            <div class="flex items-start justify-between">
                                <span class="text-sm font-medium text-gray-900 dark:text-slate-100">
                                    {{ check.label }}
                                </span>
                                <span
                                    v-if="check.severity"
                                    :class="getSeverityClass(check.severity)"
                                >
                                    {{ getSeverityLabel(check.severity) }}
                                </span>
                            </div>
                            <span class="mt-1 text-xs text-gray-500 dark:text-slate-400">
                                {{ check.description }}
                            </span>
                            <div v-if="check.current_value !== undefined || check.expected_value" class="mt-2 flex flex-wrap gap-3 text-xs">
                                <div v-if="check.current_value !== undefined" class="flex items-center gap-1.5">
                                    <span class="font-medium text-gray-600 dark:text-slate-400">Valor atual:</span>
                                    <span class="rounded bg-gray-100 px-2 py-0.5 font-mono text-gray-800 dark:bg-slate-700 dark:text-slate-200">
                                        {{ formatCheckValue(check.current_value) }}
                                    </span>
                                </div>
                                <div v-if="check.expected_value" class="flex items-center gap-1.5">
                                    <span class="font-medium text-gray-600 dark:text-slate-400">Esperado:</span>
                                    <span class="rounded bg-gray-100 px-2 py-0.5 font-mono text-gray-800 dark:bg-slate-700 dark:text-slate-200">
                                        {{ check.expected_value }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-4 grid grid-cols-3 gap-3 rounded-lg bg-gray-50 p-3 dark:bg-slate-900/50">
                    <div class="text-center">
                        <p class="text-2xl font-bold text-green-600 dark:text-green-400">
                            {{ checklistData.summary?.passed || 0 }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-slate-400">Aprovados</p>
                    </div>
                    <div class="text-center">
                        <p class="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                            {{ checklistData.summary?.warnings || 0 }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-slate-400">Alertas</p>
                    </div>
                    <div class="text-center">
                        <p class="text-2xl font-bold text-red-600 dark:text-red-400">
                            {{ checklistData.summary?.failed || 0 }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-slate-400">Reprovados</p>
                    </div>
                </div>
            </div>

            <div v-else class="mt-4 text-center text-sm text-gray-500 dark:text-slate-400">
                Nenhuma checklist disponível
            </div>

            <div class="mt-6 flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-4 dark:border-slate-700">
                <button
                    class="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition hover:border-gray-300 hover:text-gray-800 dark:border-slate-600 dark:text-slate-300 dark:hover:border-slate-500 dark:hover:text-slate-100"
                    @click="loadChecklist"
                    :disabled="loading"
                >
                    <ArrowPathIcon class="-ml-1 mr-2 inline h-4 w-4" :class="loading ? 'animate-spin' : ''" />
                    Atualizar Análise
                </button>
            </div>
        </div>

        <div
            v-if="processing"
            class="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-white/80 dark:bg-slate-800/80"
        >
            <ticto-loading size="sm" />
        </div>
    </div>
</template>

<script>
import axios from "axios";
import TictoLoading from "../../layouts/TictoLoading.vue";
import {
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ChevronDownIcon,
    UserIcon,
    ChartBarIcon,
    ShieldCheckIcon,
    ClipboardDocumentCheckIcon,
    GlobeAltIcon,
    ArrowPathIcon,
    LockClosedIcon,
    CreditCardIcon,
    ShoppingCartIcon,
    UserCircleIcon,
} from "@heroicons/vue/24/outline";

export default {
    name: "CashoutChecklist",
    components: {
        TictoLoading,
        CheckCircleIcon,
        XCircleIcon,
        ExclamationTriangleIcon,
        InformationCircleIcon,
        ChevronDownIcon,
        ArrowPathIcon,
    },
    props: {
        cashoutId: {
            type: [String, Number],
            required: true,
        },
        processing: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            error: null,
            filterStatus: 'issues',
            checklistData: {
                final_score: 0,
                approved_checks: 0,
                review_checks: 0,
                failed_checks: 0,
                total_orders: 0,
                orders_volume: "low",
                status: "pending",
                categories: {},
                checks: [],
                summary: {
                    total_checks: 0,
                    passed: 0,
                    failed: 0,
                    warnings: 0,
                },
                recommendation: "",
            },
        };
    },
    computed: {
        circumference() {
            return 2 * Math.PI * 18;
        },
        progressOffset() {
            const progress = (100 - (this.checklistData.final_score || 0)) / 100;
            return this.circumference * progress;
        },
        scoreLabel() {
            const score = this.checklistData.final_score || 0;
            if (score >= 85) return "Baixo Risco";
            if (score >= 70) return "Risco Moderado";
            return "Alto Risco";
        },
        scoreColorClass() {
            const score = this.checklistData.final_score || 0;
            if (score >= 85) return "text-green-600 dark:text-green-400";
            if (score >= 70) return "text-yellow-600 dark:text-yellow-400";
            return "text-red-600 dark:text-red-400";
        },
        statusBadgeClass() {
            const ratio = this.passedCount / Math.max(this.totalCount, 1);
            if (this.failedCount > 2) return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
            if (ratio >= 0.8) return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300";
            if (ratio >= 0.5) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300";
            return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
        },
        recommendationClass() {
            const status = this.checklistData.status;
            if (status === "approved") return "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800";
            if (status === "rejected") return "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800";
            return "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800";
        },
        recommendationIconClass() {
            const status = this.checklistData.status;
            if (status === "approved") return "text-green-600 dark:text-green-400";
            if (status === "rejected") return "text-red-600 dark:text-red-400";
            return "text-yellow-600 dark:text-yellow-400";
        },
        recommendationTextClass() {
            const status = this.checklistData.status;
            if (status === "approved") return "text-green-800 dark:text-green-200";
            if (status === "rejected") return "text-red-800 dark:text-red-200";
            return "text-yellow-800 dark:text-yellow-200";
        },
        totalCount() {
            return this.checklistData.checks?.length || 0;
        },
        passedCount() {
            return this.checklistData.approved_checks || this.checklistData.summary?.passed || 0;
        },
        failedCount() {
            return this.checklistData.failed_checks || this.checklistData.summary?.failed || 0;
        },
        warningCount() {
            return this.checklistData.review_checks || this.checklistData.summary?.warnings || 0;
        },
        issuesCount() {
            return this.failedCount + this.warningCount;
        },
        filteredChecks() {
            if (this.filterStatus === 'all') {
                return this.checklistData.checks || [];
            }
            return (this.checklistData.checks || []).filter(
                check => check.status === 'failed' || check.status === 'warning'
            );
        },
    },
    mounted() {
        this.loadChecklist();
    },
    methods: {
        async loadChecklist() {
            this.loading = true;
            this.error = null;

            try {
                const { data } = await axios.get(`/api/cashout/${this.cashoutId}/checklist`);

                // Map API response to component structure
                if (data.checks && Array.isArray(data.checks)) {
                    data.checks = data.checks.map(check => ({
                        ...check,
                        id: check.type || check.id,
                        label: check.title || check.label,
                        status: this.mapApiStatus(check.status),
                    }));
                }

                // Calculate summary if not provided
                if (!data.summary && data.checks) {
                    data.summary = {
                        total_checks: data.checks.length,
                        passed: data.checks.filter(c => c.status === 'passed').length,
                        warnings: data.checks.filter(c => c.status === 'warning').length,
                        failed: data.checks.filter(c => c.status === 'failed').length,
                    };
                }

                // Build categories dynamically from checks if not provided
                if (!data.categories && data.checks) {
                    data.categories = this.buildCategoriesFromChecks(data.checks);
                }

                this.checklistData = data;
            } catch (error) {
                console.error("Erro ao carregar checklist:", error);
                this.error = error.response?.data?.message || "Erro ao carregar checklist. Tente novamente.";
            } finally {
                this.loading = false;
            }
        },
        mapApiStatus(status) {
            // Map API status to component expected format
            switch (status) {
                case 'approved':
                    return 'passed';
                case 'review':
                    return 'warning';
                case 'failed':
                case 'rejected':
                    return 'failed';
                default:
                    return status || 'warning';
            }
        },
        buildCategoriesFromChecks(checks) {
            // Group checks by risk_level to create dynamic categories
            const categories = {};

            checks.forEach(check => {
                const riskLevel = check.risk_level || 'Outros';
                const categoryKey = this.normalizeRiskLevel(riskLevel);

                if (!categories[categoryKey]) {
                    categories[categoryKey] = {
                        label: riskLevel,
                        icon: this.getRiskLevelIcon(riskLevel),
                        checks: []
                    };
                }

                categories[categoryKey].checks.push(check);
            });

            return categories;
        },
        normalizeRiskLevel(riskLevel) {
            // Normalize risk level to use as key
            return riskLevel.toLowerCase()
                .replace(/[áàãâ]/g, 'a')
                .replace(/[éèê]/g, 'e')
                .replace(/[íìî]/g, 'i')
                .replace(/[óòõô]/g, 'o')
                .replace(/[úùû]/g, 'u')
                .replace(/ç/g, 'c')
                .replace(/\s+/g, '_');
        },
        getRiskLevelIcon(riskLevel) {
            // Map risk levels to appropriate icons
            const lowerRisk = riskLevel.toLowerCase();
            if (lowerRisk.includes('crítico') || lowerRisk.includes('alto')) {
                return 'shield-check';
            } else if (lowerRisk.includes('médio')) {
                return 'chart-bar';
            } else if (lowerRisk.includes('baixo')) {
                return 'clipboard-document-check';
            } else if (lowerRisk.includes('positiv')) {
                return 'user-check';
            }
            return 'globe-alt';
        },
        getCategoryStatus(checks) {
            if (!checks || !checks.length) return "Sem verificações";

            const passed = checks.filter((c) => c.status === "passed").length;
            const failed = checks.filter((c) => c.status === "failed").length;
            const warnings = checks.filter((c) => c.status === "warning").length;

            const parts = [];
            if (passed > 0) parts.push(`${passed} OK`);
            if (warnings > 0) parts.push(`${warnings} alertas`);
            if (failed > 0) parts.push(`${failed} falhas`);

            return parts.join(" • ") || "Pendente";
        },
        getCategoryIcon(icon) {
            const icons = {
                "user-check": UserIcon,
                "chart-bar": ChartBarIcon,
                "shield-check": ShieldCheckIcon,
                "clipboard-check": ClipboardDocumentCheckIcon,
                "globe": GlobeAltIcon,
                "lock-closed": LockClosedIcon,
                "credit-card": CreditCardIcon,
                "shopping-cart": ShoppingCartIcon,
                "user-circle": UserCircleIcon,
            };
            return icons[icon] || InformationCircleIcon;
        },
        getCategoryBadgeClass(checks) {
            if (!checks || !checks.length) return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";

            const failed = checks.filter(c => c.status === 'failed').length;
            const warnings = checks.filter(c => c.status === 'warning').length;
            const passed = checks.filter(c => c.status === 'passed').length;

            if (failed > 0) {
                return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300";
            } else if (warnings > 0) {
                return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300";
            } else if (passed === checks.length) {
                return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300";
            }
            return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
        },
        getCategoryPassedCount(checks) {
            if (!checks || !checks.length) return 0;
            return checks.filter(c => c.status === 'passed').length;
        },
        getCheckBorderClass(status) {
            switch(status) {
                case 'passed':
                    return "border-gray-200 dark:border-slate-700 hover:border-green-300 dark:hover:border-green-800";
                case 'failed':
                    return "border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20";
                case 'warning':
                    return "border-yellow-200 bg-yellow-50/50 dark:border-yellow-900 dark:bg-yellow-950/20";
                default:
                    return "border-gray-200 dark:border-slate-700";
            }
        },
        getSeverityClass(severity) {
            switch (severity) {
                case "danger":
                    return "ml-auto rounded bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700 dark:bg-red-900/40 dark:text-red-300";
                case "warning":
                    return "ml-auto rounded bg-yellow-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300";
                default:
                    return "";
            }
        },
        getSeverityLabel(severity) {
            switch (severity) {
                case "danger":
                    return "Crítico";
                case "warning":
                    return "Atenção";
                default:
                    return "";
            }
        },
        formatCheckValue(value) {
            if (typeof value === 'boolean') {
                return value ? 'true' : 'false';
            }
            if (typeof value === 'number') {
                return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
            }
            return value;
        },
    },
};
</script>